import { useParams, useNavigate, NavLink } from "react-router-dom";
import { loadingActive, pageLoadingActive } from "../../components/Background";
import { ChatViewer, MessageWriteComponent } from "../../components/MessageComponents";
import { useState, useEffect, useContext } from "react";
import { formatTimestamp, setImageUrl } from "../../utils";
import { MdDone } from "react-icons/md";
import { BanUserButton, CategoryBubble, PriorityBubble, SideActionButton, StatusBubble, TicketAssignModal, TicketAttrEditRow, TicketAttrRow, TicketPutOnHoldModal, TicketReopenModal, TicketRetainAttachmentsModal, TicketSubmitAndCloseWarningModal, TicketSubmitAndResolveWarningModal } from "../../components/TicketComponents";
import { AccountContext } from "../../contexts/AccountContexts";
import { NoteViewer } from "../../components/NotesComponents";
import ImageViewer from "../../components/ImageComponents";
import { CiEdit } from "react-icons/ci";
import { IoCloseSharp, IoShieldSharp } from "react-icons/io5";
import { IoIosPause, IoIosPerson } from "react-icons/io";
import { MdDoneOutline } from "react-icons/md";
import { VscIssueReopened } from "react-icons/vsc";
import { MdOutlineSentimentVeryDissatisfied } from "react-icons/md";
import { RiPushpinFill } from "react-icons/ri";

import { sendMessage, sendMessageWithFileLinks, uploadFiles } from "../../utils/messagingUtils";
import { NotificationContext } from "../../contexts/NotificationContexts";
import { ContentContext } from "../../contexts/ContentContext";
import { requestEditTicket, requestFetchTicketsLast5, requestSubmitCompleteTicket, requestTicket } from "../../requests/ticket_requests";
import { TimePassedComponent } from "../../components/TimeComponents";
import { PageHeader } from "../../components/HeaderComponents";
import { useTranslation } from "react-i18next";
import { BreadCrumbsGeneral } from "../../components/NavigationComponents";
import { BanHistory, BanModal } from "../../components/BanComponents";
import { requestNewChatMessage } from "../../requests/chat_requests";
import { checkDontShowAgain } from "../../components/ModalComponents";
import { MemberProfileComponent } from "../../components/DeveloperComponents";
import { FiShield, FiShieldOff } from "react-icons/fi";


const EditTicket = () => {
    const { t } = useTranslation('edit')
    const { ticket_info } = useParams();

    const { user, devs, userPreferences, language } = useContext(AccountContext)
    const { socket, isSidebar, setIsSidebar } = useContext(NotificationContext)
    const { categories, priorities, servers, deletedServers, openErrorModal } = useContext(ContentContext)
    const [categoryOptions, setCategoryOptions] = useState({})

    const [ticketInfo, setTicketInfo] = useState(null)

    const [completeAction, setCompleteAction] = useState(false)
    const autoRedirect = userPreferences ? userPreferences.redirect_after_ticket_complete : false

    // Ticket edit variables
    const [editedCategory, setEditedCategory] = useState(null);
    const [editedPriority, setEditedPriority] = useState(null);

    const [editWindowOpen, setEditWindowOpen] = useState(false)

    const [newMessage, setNewMessage] = useState("");
    const [newFiles, setNewFiles] = useState([]);

    const [anonUser, setAnonUser] = useState(null);

    const [initLoading, setInitLoading] = useState(true)

    // The reopen ticket function either through reply of fast action
    const [isByReply, setIsByReply] = useState(false)
    const [isSendingMessage, setIsSendingMessage] = useState(false)
    const [replyTo, setReplyTo] = useState(null)
    const [lastTickets, setLastTickets] = useState([])
    const [isLastTicketsOpen, setIsLastTicketsOpen] = useState(window.innerWidth >= 1024)
    const [isNotesOpen, setIsNotesOpen] = useState(false)
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024)

    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth >= 1024)
            if (window.innerWidth >= 1024) setIsLastTicketsOpen(true)
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const navigator = useNavigate();


    useEffect(() => {
        if (isSidebar) {
            setIsSidebar(false)
        }

        socket.off('ticket_assigned')
        socket.on('ticket_assigned', (pckg) => {
            setTicketInformation({ ticketData: pckg.ticket })
        })

    }, [])


    const setNotesId = (notesId) => {
        setTicketInfo({
            ...ticketInfo,
            notes_id: notesId
        })
    }


    const setTicketInformation = ({ ticketData, userData }) => {
        setTicketInfo(ticketData)
        if (userData) {
            setAnonUser(userData)
        }
    }

    useEffect(() => {
        if (completeAction && autoRedirect) {
            setTimeout(() => {
                navigator("/admin/ticket")
            }, 100)
            setCompleteAction(false)
        }
    }, [completeAction])


    useEffect(() => {
        let temp = categories;
        Object.values(categories).forEach((c) => {
            temp[c.id].title = c?.locales?.[language] ? c?.locales?.[language] : c.title
        })
        setCategoryOptions((prev) => ({ ...temp }))
    }, [categories])


    useEffect(() => {
        // Upon opening the page decode the ticket_info and make a ticket info request

        pageLoadingActive(true)
        const decoded = JSON.parse(atob(ticket_info));
        requestTicket({ ticketId: decoded.ticketId, ticketEmail: decoded.email, ticketNumber: decoded?.ticketNumber })
            .then((data) => {
                setTicketInformation({ ticketData: data.ticket })
                setAnonUser(data.user)
                pageLoadingActive(false)
                socket.emit('user_ticket_visit', { 'ticket_id': decoded.ticketId })
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)
            })
            .finally(() => {
                setIsSendingMessage(false)
            })
    }, [ticket_info])


    const handleSendMessage = (message, files) => {
        setIsSendingMessage(true)
        setNewMessage(message)
        setNewFiles(files)
        if (ticketInfo.status === "RESOLVED" || ticketInfo.status === "CLOSED") {
            setIsByReply(true)
            document.getElementById("ticket-reopen-modal").showModal()
            setIsSendingMessage(false)
            return false
        }
        else {
            if (files.length > 0) {
                sendMessageWithFileLinks({ socket: socket, message: message, files: files, ticketInfo: ticketInfo, user: user, replyTo: replyTo?.type === "message" ? replyTo : null }).then(() => {
                    setIsSendingMessage(false)
                    replyTo?.type === "message" && setReplyTo(null)
                })
            }
            else {
                sendMessage({ socket: socket, message: message, ticketInfo: ticketInfo, user: user, replyTo: replyTo?.type === "message" ? replyTo : null }).then(() => {
                    setIsSendingMessage(false)
                    replyTo?.type === "message" && setReplyTo(null)
                })
            }
            return true
        }
    }

    const editSubmitDisabledCondition = ticketInfo && (editedCategory === ticketInfo.category && editedPriority === ticketInfo.priority)

    const onSubmitEdit = () => {
        pageLoadingActive(true)
        requestEditTicket({ ticketId: ticketInfo.id, editedCategory: editedCategory, editedPriority, editedPriority }).then((data) => {
            setTicketInformation({ ticketData: data.ticket })
            pageLoadingActive(false)
            onCloseEdit()

        })
    }

    const onCloseEdit = () => {
        setEditWindowOpen(false)
        setEditedCategory(null)
        setEditedPriority(null)
    }


    const getData = (message, files) => {
        setNewMessage(message)
        setNewFiles(files)
    }

    const [completeInProgress, setCompleteInProgress] = useState(false)

    const handleSendAndResolve = async (msg, fls) => {
        loadingActive(true)
        let fileUrls = []
        if (fls && fls.length > 0) {
            const fileUploadResponse = await uploadFiles(fls);
            fileUrls = fileUploadResponse.data.fileUrls
        }
        else if (newFiles.length > 0) {
            const fileUploadResponse = await uploadFiles(newFiles);
            fileUrls = fileUploadResponse.data.fileUrls
        }
        setCompleteInProgress(true)
        requestNewChatMessage({ chatId: ticketInfo.chat_id, user_class: user.class, message: msg ?? newMessage, sender_id: user.id, sender_name: user.name, ticket_id: ticketInfo.id, attachments: fileUrls, replyTo: replyTo })
            .then(() => {
                requestSubmitCompleteTicket({ ticketInfo: ticketInfo, action: "RESOLVED", excuse: "", isUnAnswered: false })
                    .then((data) => {
                        setTicketInformation({ ticketData: data.ticket_data })
                        document.getElementById("ticket-submit-and-resolve-warning-modal").close()
                        setCompleteAction(true)
                        setReplyTo(null)
                    })
                    .catch((error) => {
                        console.error(error.status, error.message)
                        openErrorModal(error)

                    })
                    .finally(() => {
                        setCompleteInProgress(false)
                        loadingActive(false)
                        setNewMessage("")
                        setNewFiles([]);
                        setIsSendingMessage(false)
                    })
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)

            })
    }


    const handleSendAndClose = async (msg, fls) => {
        loadingActive(true)
        let fileUrls = []
        if (fls && fls.length > 0) {
            const fileUploadResponse = await uploadFiles(fls);
            fileUrls = fileUploadResponse.data.fileUrls
        }
        else if (newFiles.length > 0) {
            const fileUploadResponse = await uploadFiles(newFiles);
            fileUrls = fileUploadResponse.data.fileUrls
        }
        setCompleteInProgress(true)
        requestNewChatMessage({ chatId: ticketInfo.chat_id, user_class: user.class, message: msg ?? newMessage, sender_id: user.id, sender_name: user.name, ticket_id: ticketInfo.id, attachments: fileUrls, replyTo: replyTo })
            .then(() => {
                requestSubmitCompleteTicket({ ticketInfo: ticketInfo, action: "CLOSED", excuse: "", isUnAnswered: false })
                    .then((data) => {
                        setTicketInformation({ ticketData: data.ticket_data })
                        document.getElementById("ticket-submit-and-close-warning-modal").close()
                        setCompleteAction(true)
                        setReplyTo(null)
                    })
                    .catch((error) => {
                        console.error(error.status, error.message)
                        openErrorModal(error)
                    })
                    .finally(() => {
                        setCompleteInProgress(false)
                        loadingActive(false)
                        setNewMessage("")
                        setNewFiles([]);
                        setIsSendingMessage(false)
                    })
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)

            })

    }

    const handleReopen = () => {
        pageLoadingActive(true)
        if (newFiles.length > 0) {
            sendMessageWithFileLinks({ socket: socket, message: newMessage, files: newFiles, ticketInfo: ticketInfo, user: user, replyTo: replyTo?.type === "message" ? replyTo : null })
        }
        else {
            sendMessage({ socket: socket, message: newMessage, ticketInfo: ticketInfo, user: user, replyTo: replyTo })
        }
        setNewMessage("#FINISHED#")
        setNewFiles([]);
        pageLoadingActive(false)
        setReplyTo(null)
    }


    const handleSendAndComplete = (resolved, message, files) => {
        if (ticketInfo.status === "CLOSED" || ticketInfo.status === "RESOLVED") {
            return
        }
        setNewMessage(message)
        setNewFiles(files)
        if (resolved) {
            const isDontShow = checkDontShowAgain(user.id, "ticket-resolve-warning-modal")
            if (isDontShow) {
                handleSendAndResolve(message, files)
            }
            else {
                document.getElementById("ticket-submit-and-resolve-warning-modal").showModal()
            }
        }
        else {
            const isDontShow = checkDontShowAgain(user.id, "ticket-close-warning-modal")
            if (isDontShow) {
                handleSendAndClose(message, files)
            }
            else {
                document.getElementById("ticket-submit-and-close-warning-modal").showModal()
            }
        }
    }

    useEffect(() => {
        if (anonUser?.id) {
            requestFetchTicketsLast5({ userId: anonUser.id })
                .then((data) => {
                    setLastTickets(data.tickets);
                })
                .catch(err => console.error("Geçmiş biletler yüklenemedi", err));
        }
    }, [anonUser?.id]);

    if (anonUser == null || ticketInfo === null) {
        return <></>
    }


    if (initLoading) {
        setInitLoading(false)
    }


    const breadCrumbs = [
        {
            link: "/admin/dashboard",
            name: t("Home")
        },
        {
            link: "/admin/ticket",
            name: t("Tickets")
        },
        {
            link: null,
            name: ticketInfo.createdAt
        }
    ]

    return <>
        <TicketAssignModal socket={socket} currentCategory={ticketInfo.category} setTicketInformation={setTicketInformation} ticketId={ticketInfo.id} ticketServer={ticketInfo.server} chatId={ticketInfo.chat_id} assignedTo={ticketInfo.assignedTo} />
        <TicketReopenModal ticketInfo={ticketInfo} setTicketInformation={setTicketInformation} isByReply={isByReply} messageFunction={() => { handleReopen() }} />
        <TicketPutOnHoldModal ticketInfo={ticketInfo} setTicketInformation={setTicketInformation} />
        <TicketSubmitAndResolveWarningModal completeInProgress={completeInProgress} ticketInfo={ticketInfo} callback={handleSendAndResolve} />
        <TicketSubmitAndCloseWarningModal newMessage={newMessage} ticketInfo={ticketInfo} callback={handleSendAndClose} />
        <TicketRetainAttachmentsModal ticketInfo={ticketInfo} setTicketInformation={setTicketInformation} />
        <BanModal anonUser={anonUser} ticketInfo={ticketInfo} setTicketInformation={setTicketInformation} />

        <main className="flex flex-col w-full py-6 gap-6 px-4 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 min-h-screen font-sans">
            <PageHeader pageTitle={`${t('Ticket')} - ${ticketInfo.createdAt}`} />

            {/* Top Bar: Title, Breadcrumbs, Assigned To */}
            <div className="flex flex-col md:flex-row w-full justify-between items-start md:items-center gap-4">
                <div className="flex flex-col gap-1">
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">{t('Ticket')} #{ticketInfo.id || ticketInfo.createdAt}</h1>
                    <BreadCrumbsGeneral elements={breadCrumbs} />
                </div>
            </div>

            {/* 2x2 Desktop Grid Layout Restructure */}
            <div className="flex flex-col gap-6 w-full">

                {/* TOP ROW: Ticket Details & Last Tickets (2 Columns) */}
                <div className="grid grid-cols-1 xl:grid-cols-[5fr_3fr] gap-2 items-stretch w-full">

                    <div className="p-6 rounded-xl col-span-2 border-slate-100 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/50 flex flex-col lg:flex-row gap-6 justify-between items-start lg:items-center flex-shrink-0">

                        {/* User Profile Info */}
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0">
                                <MemberProfileComponent member={anonUser?.rank === "user" && anonUser?.type !== "temp" && anonUser} ticket={ticketInfo} />
                            </div>
                            <div className="flex items-center gap-4">
                                <span className={`font-medium ${anonUser.banned ? 'text-red-600 dark:text-red-500' : 'text-slate-900 dark:text-white'}`}>
                                    {ticketInfo.email}
                                    {anonUser.banned && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">{t("Banned")}</span>}
                                </span>
                                {ticketInfo && anonUser && <div className="mt-1"><BanHistory ticketInfo={ticketInfo} ticketUser={anonUser} /></div>}
                            </div>
                        </div>

                        {/* Actions Group */}
                        <div className="flex flex-wrap items-center gap-2">
                            {!editWindowOpen && (
                                <>
                                    <SideActionButton key="init-edit-key" title={t("Edit ticket")} disabled={!(ticketInfo.status === "RESOLVED" || ticketInfo.status === "CLOSED") ? (ticketInfo.assignedTo !== user.id && user.rank !== "admin") : (ticketInfo.closed_by !== user.id && ticketInfo.resolved_by !== user.id && user.rank !== "admin")} content={<CiEdit size={18} />} callback={() => { setEditWindowOpen(!editWindowOpen) }} />
                                    <SideActionButton key="assign-to-another-person-key" title={t("Assign to user")} disabled={ticketInfo.status === "RESOLVED" || ticketInfo.status === "CLOSED" || (ticketInfo.assignedTo !== user.id && user.rank !== "admin")} content={<IoIosPerson size={18} />} callback={() => document.getElementById('ticket-assign-modal').showModal()} />
                                    <SideActionButton key="ticket-put-on-hold-key" title={t("Put ticket on hold")} disabled={ticketInfo.status === "RESOLVED" || ticketInfo.status === "CLOSED" || ticketInfo.status === "ON HOLD" || (ticketInfo.assignedTo !== user.id && user.rank !== "admin")} content={<IoIosPause size={18} />} callback={() => document.getElementById('ticket-put-on-hold-modal').showModal()} />
                                    <BanUserButton key="ban-unban-user-key" title={anonUser.banned ? t("Unban user") : t("Ban user")} disabled={ticketInfo.status === "RESOLVED" || (ticketInfo.assignedTo !== user.id && user.rank !== "admin" && ticketInfo.closed_by !== user.id)} user={anonUser} ticket={ticketInfo} getData={setTicketInformation} />
                                    {!ticketInfo?.clean && (
                                        <SideActionButton key="retain-attachments-key" title={ticketInfo?.retain_attachments ? t("Remove protection") : t("Retain attachments")} disabled={false} content={ticketInfo?.retain_attachments ? <FiShieldOff size={18} /> : <FiShield size={18} color="#16a34a" />} callback={() => document.getElementById("ticket-retain-attachments-modal").showModal()} />
                                    )}
                                    {ticketInfo.status === "RESOLVED" || ticketInfo.status === "CLOSED" ? (
                                        <SideActionButton key="reopen-closed-ticket-key" title={t("Reopen ticket")} disabled={!Object.keys(categories).includes(ticketInfo.category)} content={<VscIssueReopened size={18} />} callback={() => { setIsByReply(false); document.getElementById('ticket-reopen-modal').showModal() }} />
                                    ) : (
                                        <SideActionButton key="complete-ticket-fast-action-key" title={t("Fast complete ticket")} disabled={ticketInfo.assignedTo !== user.id && user.rank !== "admin"} content={<MdDoneOutline size={18} />} callback={() => document.getElementById('ticket-fast-complete-modal').showModal()} />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    {/* Ticket Details Box */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col w-full h-full overflow-hidden">


                        {/* Metadata Row */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-6 border-b border-slate-100 dark:border-slate-700/50 flex-shrink-0">
                            <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{t("Server")}</p>
                                <p className="text-sm font-medium">{servers[ticketInfo.server]?.title ?? deletedServers[ticketInfo.server]?.title ?? ticketInfo.server}</p>
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{t("Character Name")}</p>
                                <p className="text-sm font-medium">{ticketInfo.characterName || '-'}</p>
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{t("Status")}</p>
                                <StatusBubble ticketStatus={ticketInfo.status} />
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{t("Timeline")}</p>
                                <div className="flex flex-col text-xs space-y-1">
                                    <div className="text-slate-500 flex flex-col">
                                        <span>{t("Created At")}</span>
                                        <span>{formatTimestamp(ticketInfo.createdAt * 1000)}</span>
                                    </div>
                                    <div className="font-semibold text-indigo-600 dark:text-indigo-400 flex flex-col">
                                        <span>{t("Last Update")}</span>
                                        <div className="flex items-center gap-2">
                                            <span>{formatTimestamp(ticketInfo.lastUpdated * 1000)}</span>
                                            <TimePassedComponent key={ticketInfo.id} timestamp={ticketInfo.lastUpdated * 1000} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Edit Window Overlay / Properties */}
                        <div className="p-6 border-b border-slate-100 dark:border-slate-700/50 flex-shrink-0">
                            {editWindowOpen ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end bg-indigo-50/50 dark:bg-indigo-900/10 p-5 rounded-xl border border-indigo-100 dark:border-indigo-800/30">
                                    <TicketAttrEditRow attrName={t("Category")} selectedAttr={ticketInfo.category} placeHolder={t("Select new category")} options={user.rank === "admin" ? Object.keys(categories) : user.categories} setNew={setEditedCategory} collection={categoryOptions} />
                                    <TicketAttrEditRow attrName={t("Priority")} selectedAttr={ticketInfo.priority} placeHolder={t("Select new priority level")} options={Object.keys(priorities)} setNew={setEditedPriority} collection={priorities} />
                                    <div className="md:col-span-2 flex justify-end gap-3 mt-4">
                                        <button onClick={onCloseEdit} className="px-5 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-700 transition shadow-sm">
                                            {t("Cancel")}
                                        </button>
                                        <button onClick={onSubmitEdit} disabled={editSubmitDisabledCondition} className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm">
                                            {t("Save Changes", { defaultValue: "Save Changes" })}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-[1fr_1fr_2fr] gap-x-8 gap-y-4">
                                    <div className="flex flex-col items-start gap-3">
                                        <span className="text-sm font-medium text-slate-500">{t("Category")}:</span>
                                        <CategoryBubble ticketCategory={ticketInfo.category} />
                                    </div>
                                    <div className="flex flex-col items-start gap-3">
                                        <span className="text-sm font-medium text-slate-500">{t("Priority")}:</span>
                                        <PriorityBubble ticketPriority={ticketInfo.priority} />
                                    </div>
                                    {(ticketInfo.status === "RESOLVED" || ticketInfo.status === "CLOSED") && ticketInfo.excuse && (
                                        <div className="flex flex-col items-start gap-3 w-full mt-2">
                                            <span className="text-sm font-medium text-slate-500 whitespace-nowrap">{t("Reason")}:</span>
                                            <span className="text-sm text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700/50 px-4 py-2 rounded-lg w-full">{t(ticketInfo.excuse)}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {ticketInfo.violatersName && ticketInfo.violatersName !== "" && (
                                <div className="mt-5 p-4 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-lg flex items-center gap-3">
                                    <span className="text-sm font-bold text-red-700 dark:text-red-400 uppercase tracking-wide">{t("Violater's Name")}:</span>
                                    <span className="text-sm font-medium text-red-600 dark:text-red-300">{ticketInfo.violatersName}</span>
                                </div>
                            )}
                        </div>

                        {/* Description Section (Scrollable Area) */}
                        <div className="p-6 bg-slate-50/30 dark:bg-slate-800/30 flex-1 overflow-y-auto min-h-0">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4 leading-relaxed">{ticketInfo.subject}</h3>
                            <div className="prose prose-sm dark:prose-invert max-w-none text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-medium leading-relaxed">
                                {ticketInfo.description}
                            </div>
                            {ticketInfo.attachments?.length > 0 && (
                                <div className="mt-8 border-t border-slate-200 dark:border-slate-700 pt-6">
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                                        <MdDone /> {t("Attachments")}
                                    </p>
                                    <ImageViewer imageUrls={ticketInfo.attachments} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Last Tickets Card */}
                    <div className={`bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col w-full  ${isLastTicketsOpen ? 'h-[85dvh]' : 'h-16'}`}>
                        <div className="lg:hidden w-full flex-shrink-0">
                            <button onClick={() => setIsLastTicketsOpen(!isLastTicketsOpen)} className="flex w-full justify-between items-center px-5 py-5 border-b border-slate-100 dark:border-slate-700 font-bold text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                                {t("Last Tickets")}
                                <span className={`transition-transform duration-300 ${isLastTicketsOpen ? 'rotate-180' : ''}`}>▼</span>
                            </button>
                        </div>

                        <div className="hidden lg:block p-6 rounded-t-2xl border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 flex-shrink-0">
                            <h2 className="font-semibold text-lg text-slate-800 dark:text-slate-200">{t("Last Tickets")}</h2>
                        </div>

                        <div className={`flex-1 transition-all duration-300 ease-in-out ${isLastTicketsOpen ? 'opacity-100' : 'opacity-0 lg:opacity-100'} overflow-hidden flex flex-col`}>
                            <div className="flex flex-col flex-1 overflow-y-auto p-4 gap-4">
                                {lastTickets.length > 0 ? lastTickets.map((ticket) => (
                                    <NavLink
                                        key={ticket.id}
                                        to={`/admin/${ticket.fetch_code}/edit`}
                                        className={`flex flex-col p-4 rounded-xl border transition-all duration-200 flex-shrink-0 ${ticket.id === ticketInfo.id ? "bg-indigo-50 border-indigo-300 dark:bg-indigo-900/30 dark:border-indigo-700" : "bg-white border-slate-200 hover:border-indigo-300 hover:shadow-md dark:bg-slate-800 dark:border-slate-700 dark:hover:border-slate-500"}`}
                                    >
                                        <div className="flex justify-between items-start mb-3 gap-2">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{ticket.subject}</p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 line-clamp-2 leading-relaxed">{ticket.description}</p>
                                            </div>
                                            <StatusBubble ticketStatus={ticket.status} />
                                        </div>

                                        <div className="flex justify-between items-end mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/50">
                                            <div className="flex items-center gap-2">
                                                {ticket.assignedTo ? (
                                                    <div className="w-7 h-7 rounded-full overflow-hidden bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center border border-indigo-200 dark:border-indigo-800">
                                                        {devs[ticket.assignedTo]?.avatar?.length < 3 ? (
                                                            <span className="text-[10px] font-bold text-indigo-700 dark:text-indigo-300 pr-0">GM</span>
                                                        ) : (
                                                            <img src={setImageUrl(devs[ticket.assignedTo].avatar)} className="w-full h-full object-cover" alt="" />
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs font-semibold text-red-500 bg-red-50 dark:bg-red-900/10 px-2 py-1 rounded-md">{t('Unassigned', { defaultValue: 'Unassigned' })}</span>
                                                )}
                                            </div>
                                            <span className="text-xs font-semibold text-slate-400">{formatTimestamp(ticket.lastUpdated * 1000)}</span>
                                        </div>
                                    </NavLink>
                                )) : (
                                    <div className="py-10 text-center bg-slate-50 dark:bg-slate-800/50 rounded-xl m-2 border border-dashed border-slate-300 dark:border-slate-700">
                                        <span className="text-sm font-semibold text-slate-400">{t("Kayıt bulunamadı.")}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </div>

                {/* BOTTOM ROW: Chat Area & Notes (Internal Chat) (2 Columns) */}
                <div className="grid grid-cols-1 xl:grid-cols-[5fr_3fr] gap-2 items-stretch w-full xl:min-h-[600px]">

                    {/* Chat Area */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col w-full h-[15   0dvh] lg:h-[65dvh] min-h-[500px] xl:h-full overflow-hidden">
                        <div className="flex-1 overflow-hidden flex flex-col p-2 lg:p-0 min-h-0">
                            <ChatViewer socket={socket} ticketInfo={ticketInfo} chatId={ticketInfo.chat_id} firstPerson={user} anonUser={anonUser} setAnonUser={setAnonUser} dev={true} setTicketInformation={setTicketInformation} isSendingMessage={isSendingMessage} replyTo={replyTo} setReplyTo={setReplyTo} />
                        </div>
                        <div className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 lg:p-0 flex-shrink-0">
                            <MessageWriteComponent ticketInfo={ticketInfo} user={user} getData={getData} message={newMessage} completeInProgress={completeInProgress} handleSendMessage={handleSendMessage} handleSendAndComplete={handleSendAndComplete} replyTo={replyTo} setReplyTo={setReplyTo} />
                        </div>
                    </div>

                    {/* Notes (Admins Only View) */}
                    <div className={`
                        bg-white dark:bg-slate-800 shadow-2xl xl:shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col flex-shrink-0
                        transition-all duration-300 ease-out origin-bottom-right
                        fixed bottom-4 right-4 sm:right-6 bottom-6 w-[calc(100vw-32px)] sm:w-[400px] h-[95dvh] min-h-[400px] z-50 rounded-2xl
                        ${isNotesOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-90 translate-y-8 pointer-events-none'}
                        xl:relative xl:w-full xl:h-full xl:bottom-auto xl:right-auto xl:z-auto xl:opacity-100 xl:scale-100 xl:translate-y-0 xl:pointer-events-auto overflow-hidden
                    `}>
                        <div className="flex absolute z-50 right-2 top-2 items-center gap-2">
                            <button onClick={() => setIsNotesOpen(false)} className="xl:hidden p-1.5 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors">
                                <IoCloseSharp size={20} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-hidden flex flex-col p-2 lg:p-0 min-h-0 relative">
                            {/* The notes UI is designed to look standard like chat */}
                            <div className="absolute inset-0 bg-yellow-50/20 dark:bg-yellow-900/5 pointer-events-none z-0"></div>
                            <div className="relative z-10 w-full h-full flex flex-col">
                                <NoteViewer key={ticketInfo.notes_id} socket={socket} notesId={ticketInfo.notes_id} setNotesId={setNotesId} ticketInfo={ticketInfo} replyTo={replyTo} setReplyTo={setReplyTo} />
                            </div>
                        </div>
                    </div>

                    {/* Mobile Floating Button for Notes */}
                    <button
                        onClick={() => setIsNotesOpen(true)}
                        className={`
                            xl:hidden fixed bottom-6 right-4 sm:right-6 z-40 bg-amber-500 hover:bg-amber-600 text-white p-4 rounded-full shadow-xl 
                            transition-all duration-300 transform hover:scale-110 active:scale-95 flex items-center justify-center group
                            ${isNotesOpen ? 'opacity-0 scale-50 pointer-events-none' : 'opacity-100 scale-100'}
                        `}
                        aria-label={t("Open Notes")}
                    >
                        <RiPushpinFill size={28} />
                    </button>
                </div>
            </div>

            <div className="hidden">
                <a id="redirect-to-user-ticket" className="relative flex h-5 w-5" href={`/ticket/${ticket_info}`} target="_blank" rel="noreferrer">
                    <span className="relative inline-flex rounded-full h-5 w-5 bg-sky-500 text-sky-100"><MdOutlineSentimentVeryDissatisfied size={20} /></span>
                </a>
            </div>
        </main>
    </>
}


export default EditTicket;